import React, { useCallback, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import styled from 'styled-components';

const getErrorMessage = error => error?.response?.data?.detail
  || error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || 'Não foi possível importar a programação.';

const getIgnoredLines = result => result?.linhas_ignoradas || result?.erros || [];

export const formatarDataProgramacao = valor => {
  const [ano, mes, dia] = String(valor || '').slice(0, 10).split('-');
  return dia ? `${dia}/${mes}/${ano}` : '-';
};

const TIPOS = [
  { value: 'previa', titulo: 'Prévia', descricao: 'Primeira programação do dia (planilha de programação Havan). Cria as cargas da data.' },
  { value: 'definitiva', titulo: 'Definitiva', descricao: 'Quadro de embarque atualizado. Compara com a prévia e adiciona, altera ou cancela cargas — você confere antes de aplicar.' }
];

const descreverCarga = carga => [carga.numero_carga, carga.turno ? `${carga.turno}º turno` : null, carga.filial, carga.tipo_caminhao].filter(Boolean).join(' · ');

const LinhasIgnoradas = ({ linhas }) => (linhas.length ? (
  <IgnoredLines>
    <strong>Linhas ignoradas</strong>
    {linhas.map((item, index) => (
      <span key={`${item.linha || index}-${item.numero_carga || ''}`}>
        Linha {item.linha || '-'} · {item.numero_carga || '-'} · {item.motivo || 'Motivo não informado'}
      </span>
    ))}
  </IgnoredLines>
) : null);

const ResumoPrevia = ({ result }) => (
  <ImportSummary aria-live="polite">
    <strong>Importação concluída</strong>
    <SummaryGrid>
      <span>{result?.total_linhas ?? result?.totalLinhas ?? 0} linhas processadas</span>
      <span>{result?.inseridas ?? result?.inseridos ?? 0} inseridas</span>
      <span>{result?.ignoradas ?? result?.total_ignoradas ?? 0} ignoradas</span>
    </SummaryGrid>
    <LinhasIgnoradas linhas={getIgnoredLines(result)} />
  </ImportSummary>
);

const Secao = ({ titulo, total, tom, aberta = false, children }) => (total ? (
  <SecaoDetalhe open={aberta} $tom={tom}>
    <summary>{titulo} ({total})</summary>
    <SecaoConteudo>{children}</SecaoConteudo>
  </SecaoDetalhe>
) : null);

const ContadoresDefinitiva = ({ resultado }) => (
  <Contadores>
    <Contador $tom="nova">✚ {resultado.novas?.length || 0} novas</Contador>
    <Contador $tom="alterada">✎ {resultado.alteradas?.length || 0} alteradas</Contador>
    <Contador $tom="reativada">↺ {resultado.reativadas?.length || 0} reativadas</Contador>
    <Contador $tom="cancelada">✖ {resultado.canceladas?.length || 0} canceladas</Contador>
    <Contador $tom="igual">= {resultado.iguais || 0} iguais</Contador>
  </Contadores>
);

const ListaAlteracoes = ({ alteracoes = [] }) => alteracoes.map(alteracao => (
  <span key={alteracao.campo}>{alteracao.titulo}: <s>{alteracao.anterior || 'vazio'}</s> → <b>{alteracao.novo || 'vazio'}</b></span>
));

const DetalheDefinitiva = ({ resultado }) => {
  const canceladas = resultado.canceladas || [];
  const alteradas = resultado.alteradas || [];
  const reativadas = resultado.reativadas || [];
  const novas = resultado.novas || [];
  const placas = resultado.placas_divergentes || [];
  const avisos = resultado.avisos || [];
  const semMudanca = !canceladas.length && !alteradas.length && !reativadas.length && !novas.length;

  return (
    <>
      {semMudanca ? <Nota>Nada muda em relação à programação atual desta data.</Nota> : null}

      <Secao titulo="✖ Canceladas — saem da programação" total={canceladas.length} tom="cancelada" aberta>
        {canceladas.map(carga => (
          <ItemCarga key={carga.numero_carga} $destaque={carga.tem_trabalho_operador || carga.tem_vx_ativa}>
            <strong>{descreverCarga(carga)}</strong>
            {carga.tem_trabalho_operador ? <span>Já tem motorista/veículo: {[carga.motorista, carga.placa_cavalo].filter(Boolean).join(' · ') || 'preenchido pelo operador'}</span> : null}
            {carga.tem_vx_ativa ? <span>Tem pesquisa VX ativa: será cancelada na VX (se estiver em biometria não dá para cancelar; a carga só deixa de usar a pesquisa).</span> : null}
          </ItemCarga>
        ))}
      </Secao>

      <Secao titulo="✎ Alteradas" total={alteradas.length} tom="alterada" aberta>
        {alteradas.map(carga => (
          <ItemCarga key={carga.numero_carga}>
            <strong>{descreverCarga(carga)}</strong>
            <ListaAlteracoes alteracoes={carga.alteracoes} />
          </ItemCarga>
        ))}
      </Secao>

      <Secao titulo="↺ Reativadas — estavam canceladas e voltaram" total={reativadas.length} tom="reativada" aberta>
        {reativadas.map(carga => (
          <ItemCarga key={carga.numero_carga}>
            <strong>{descreverCarga(carga)}</strong>
            <ListaAlteracoes alteracoes={carga.alteracoes} />
          </ItemCarga>
        ))}
      </Secao>

      {/* Sem prévia todas são novas: a lista fica fechada para não esconder o resto. */}
      <Secao titulo="✚ Novas" total={novas.length} tom="nova" aberta={resultado.possui_programacao_anterior && novas.length <= 10}>
        {novas.map(carga => <ItemCarga key={carga.numero_carga}><strong>{descreverCarga(carga)}</strong></ItemCarga>)}
      </Secao>

      <Secao titulo="Placas divergentes — a placa da carga é mantida" total={placas.length} tom="igual">
        {placas.map(item => (
          <ItemCarga key={item.numero_carga}>
            <strong>{item.numero_carga}</strong>
            <span>Na planilha: {item.placa_planilha || '-'} · Na carga: {item.placa_atual || '-'}</span>
          </ItemCarga>
        ))}
      </Secao>

      <Secao titulo="Avisos" total={avisos.length} tom="igual">
        {avisos.map(aviso => <ItemCarga key={aviso}><span>{aviso}</span></ItemCarga>)}
      </Secao>

      <LinhasIgnoradas linhas={getIgnoredLines(resultado)} />
    </>
  );
};

const PreviaDefinitiva = ({ resultado }) => {
  const data = formatarDataProgramacao(resultado.data_programacao);

  return (
    <DefinitivaBox aria-live="polite">
      <strong>Conferência da programação definitiva de {data}</strong>
      {!resultado.possui_programacao_anterior ? (
        <Alerta role="note">Não há prévia para {data}. Todas as cargas da planilha serão criadas.</Alerta>
      ) : null}
      {resultado.possui_definitiva_anterior ? (
        <Nota>Esta data já tem uma definitiva aplicada. Só as diferenças em relação à programação atual serão aplicadas.</Nota>
      ) : null}
      <Nota>Nada foi gravado ainda. Motorista, frota, placas, terceiro e VX preenchidos pelo operador são mantidos.</Nota>
      <ContadoresDefinitiva resultado={resultado} />
      <DetalheDefinitiva resultado={resultado} />
    </DefinitivaBox>
  );
};

const ResumoDefinitivaAplicada = ({ resultado }) => (
  <ImportSummary aria-live="polite">
    <strong>Programação definitiva de {formatarDataProgramacao(resultado.data_programacao)} aplicada</strong>
    <ContadoresDefinitiva resultado={resultado} />
    {resultado.vx_invalidadas ? <Nota>{resultado.vx_invalidadas} pesquisa(s) VX de cargas canceladas foram tratadas.</Nota> : null}
    <LinhasIgnoradas linhas={getIgnoredLines(resultado)} />
  </ImportSummary>
);

const ProgramacaoHavanImportModal = ({ show, onClose, onSubmit, onPrevisualizarDefinitiva, onAplicarDefinitiva }) => {
  const [tipo, setTipo] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previa, setPrevia] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) {
      setTipo(null);
      setFile(null);
      setLoading(false);
      setResult(null);
      setPrevia(null);
      setError(null);
    }
  }, [show]);

  const handleClose = useCallback(() => {
    if (!loading) onClose?.();
  }, [loading, onClose]);

  // Trocar o tipo ou o arquivo descarta a conferência: ela só vale para o arquivo conferido.
  const limparResultado = useCallback(() => {
    setResult(null);
    setPrevia(null);
    setError(null);
  }, []);

  const handleTipo = useCallback(value => {
    setTipo(value);
    limparResultado();
  }, [limparResultado]);

  const handleChange = useCallback(event => {
    setFile(event.target.files?.[0] || null);
    limparResultado();
  }, [limparResultado]);

  const executar = useCallback(async (acao, aoConcluir) => {
    if (!file?.name?.toLowerCase().endsWith('.xlsx')) {
      setError('Selecione um arquivo Excel no formato .xlsx.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      aoConcluir(await acao(file));
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setLoading(false);
    }
  }, [file]);

  const handleSubmit = useCallback(() => {
    if (loading || result || !tipo) return;
    if (tipo === 'previa') {
      executar(arquivo => onSubmit?.(arquivo), setResult);
    } else if (!previa) {
      executar(arquivo => onPrevisualizarDefinitiva?.(arquivo), setPrevia);
    } else {
      executar(arquivo => onAplicarDefinitiva?.(arquivo), resposta => { setPrevia(null); setResult(resposta); });
    }
  }, [executar, loading, onAplicarDefinitiva, onPrevisualizarDefinitiva, onSubmit, previa, result, tipo]);

  const textoBotao = tipo === 'definitiva'
    ? (previa ? 'Aplicar programação definitiva' : 'Pré-visualizar')
    : 'Importar';

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" size="lg" scrollable>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>Importar programação Havan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TipoGrupo role="radiogroup" aria-label="Tipo de programação">
          {TIPOS.map(opcao => (
            <TipoOpcao key={opcao.value} $ativo={tipo === opcao.value}>
              <input
                type="radio"
                name="programacaoHavanTipo"
                value={opcao.value}
                aria-label={opcao.titulo}
                checked={tipo === opcao.value}
                disabled={loading}
                onChange={() => handleTipo(opcao.value)}
              />
              <span>
                <strong>{opcao.titulo}</strong>
                <small>{opcao.descricao}</small>
              </span>
            </TipoOpcao>
          ))}
        </TipoGrupo>

        <Form.Group controlId="programacaoHavanArquivo" className="mb-3">
          <Form.Label>Arquivo Excel</Form.Label>
          <Form.Control type="file" accept=".xlsx" onChange={handleChange} disabled={loading} />
          <Form.Text className="text-muted">
            {tipo === 'definitiva' ? 'Quadro de embarque .xlsx (abas PRINCIPAL e BASE).' : 'Selecione a planilha .xlsx no layout de programação Havan.'}
          </Form.Text>
        </Form.Group>

        {error ? <ImportError role="alert">{error}</ImportError> : null}
        {previa ? <PreviaDefinitiva resultado={previa} /> : null}
        {result && tipo === 'previa' ? <ResumoPrevia result={result} /> : null}
        {result && tipo === 'definitiva' ? <ResumoDefinitivaAplicada resultado={result} /> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>{result ? 'Fechar' : 'Cancelar'}</Button>
        <ButtonWithLoading
          variant="primary"
          onClick={handleSubmit}
          loading={loading ? 'true' : undefined}
          disabled={loading || !!result || !tipo}
          title={!tipo ? 'Escolha se a planilha é a prévia ou a definitiva' : undefined}
        >
          {textoBotao}
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
  );
};

export default ProgramacaoHavanImportModal;

const TONS = {
  nova: { borda: '#9fd3ad', fundo: '#f1faf3', texto: '#1f5c30' },
  alterada: { borda: '#9ec3e6', fundo: '#f1f7fd', texto: '#163455' },
  reativada: { borda: '#c6b3e6', fundo: '#f7f3fd', texto: '#4b2a7a' },
  cancelada: { borda: '#f0a7ad', fundo: '#fff4f5', texto: '#a61e2a' },
  igual: { borda: '#d7e0ea', fundo: '#f8fafc', texto: '#526275' }
};

const ImportError = styled.div`padding: 10px 12px; border: 1px solid #f0a7ad; border-radius: 4px; background: #fff4f5; color: #a61e2a; font-size: 13px;`;
const ImportSummary = styled.div`display: flex; flex-direction: column; gap: 8px; padding: 14px; border: 1px solid #b9dfc1; border-radius: 4px; background: #f4fbf5; color: #23452b; font-size: 13px;`;
const SummaryGrid = styled.div`display: flex; flex-wrap: wrap; gap: 12px; font-weight: 600;`;
const IgnoredLines = styled.div`display: flex; flex-direction: column; gap: 4px; margin-top: 4px; padding-top: 12px; border-top: 1px solid #cce5d0; color: #4d5c50;`;
const TipoGrupo = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-bottom: 14px;`;
const TipoOpcao = styled.label`
  display: flex; align-items: flex-start; gap: 10px; margin: 0; padding: 12px;
  border: 2px solid ${({ $ativo }) => ($ativo ? '#163455' : '#d7e0ea')}; border-radius: 6px;
  background: ${({ $ativo }) => ($ativo ? '#eef3f8' : '#ffffff')}; cursor: pointer;
  input { margin-top: 3px; accent-color: #163455; }
  span { display: flex; flex-direction: column; gap: 3px; }
  strong { color: #163455; font-size: 14px; }
  small { color: #526275; font-size: 12px; line-height: 1.35; }
`;
const DefinitivaBox = styled.div`display: flex; flex-direction: column; gap: 10px; padding: 14px; border: 1px solid #d7e0ea; border-radius: 4px; background: #ffffff; color: #24344d; font-size: 13px;`;
const Alerta = styled.div`padding: 8px 10px; border: 1px solid #e0b44c; border-radius: 4px; background: #fff8e6; color: #8a5a00; font-weight: 600;`;
const Nota = styled.div`color: #526275; font-size: 12px;`;
const Contadores = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;
const Contador = styled.span`
  padding: 3px 10px; border: 1px solid ${({ $tom }) => TONS[$tom].borda}; border-radius: 999px;
  background: ${({ $tom }) => TONS[$tom].fundo}; color: ${({ $tom }) => TONS[$tom].texto}; font-size: 12px; font-weight: 700;
`;
const SecaoDetalhe = styled.details`
  border: 1px solid ${({ $tom }) => TONS[$tom].borda}; border-radius: 4px; background: ${({ $tom }) => TONS[$tom].fundo};
  summary { padding: 7px 10px; color: ${({ $tom }) => TONS[$tom].texto}; font-weight: 700; cursor: pointer; }
`;
const SecaoConteudo = styled.div`display: flex; flex-direction: column; gap: 6px; max-height: 260px; overflow-y: auto; padding: 0 10px 10px;`;
const ItemCarga = styled.div`
  display: flex; flex-direction: column; gap: 2px; padding: 6px 8px; border-radius: 4px; background: #ffffff;
  border-left: 3px solid ${({ $destaque }) => ($destaque ? '#c62828' : 'transparent')};
  span { color: #526275; font-size: 12px; }
  s { color: #8a96a3; }
`;
